import { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Code,
  Divider,
  Table,
  Progress,
  Anchor,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { Helmet } from "react-helmet-async";
import SettingsTitle from "@/components/settings/settings-title.tsx";
import api from "@/lib/api-client.ts";

const PHASES = [
  "phase1",
  "phase2",
  "phase3",
  "phase4",
  "calibrate",
  "generate",
  "verify",
  "route-drafts",
];

const LS_SLUG = "pipeline.lastSlug";

// Full-run pipeline order (matches the worker orchestrator). Used to show
// "шаг N из M" while the pre-generation phases run (no per-item count yet).
const STEPS: { key: string; label: string }[] = [
  { key: "clone", label: "Клонирование репозитория" },
  { key: "phase1", label: "Фильтрация файлов" },
  { key: "phase2", label: "Классификация" },
  { key: "phase3", label: "Группировка" },
  { key: "phase4", label: "Планирование страниц" },
  { key: "generate", label: "Генерация контента" },
  { key: "verify", label: "Проверка (анти-потеря данных)" },
  { key: "route-drafts", label: "Маркировка черновиков" },
];

type Prog = {
  phase?: string;
  run_status?: string;
  done?: number;
  total?: number;
  files_scanned?: number;
  files_after_filter?: number;
};

type RepoEntry = { repo: string; branch?: string };

type Status = {
  space_slug?: string;
  repo?: string;
  branch?: string;
  repos?: RepoEntry[];
  status?: string;
  current_phase?: string;
  message?: string;
  last_run_at?: string;
  pages?: number;
  pages_week?: number;
  last_page_at?: string;
  progress?: Prog | null;
  label?: string;
  aliases?: string[];
};

type OverviewRow = Status;

const color = (s?: string) =>
  s === "done"
    ? "green"
    : s === "running"
      ? "blue"
      : s === "failed"
        ? "red"
        : "gray";

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString() : "—";

export default function PipelineSettings() {
  const [slug, setSlug] = useState(
    () => localStorage.getItem(LS_SLUG) || "",
  );
  // Multi-repo: a project may live in several repositories (e.g.
  // backend + frontend). All of them belong to one space; the worker
  // scans each repo and the webhook routes events from any of them.
  const [repos, setRepos] = useState<RepoEntry[]>([
    { repo: "", branch: "main" },
  ]);
  const repoListChanged = (next: RepoEntry[]) =>
    setRepos(next.length ? next : [{ repo: "", branch: "main" }]);
  const [label, setLabel] = useState("");
  const [aliases, setAliases] = useState("");
  const [st, setSt] = useState<Status | null>(null);
  const [rows, setRows] = useState<OverviewRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadOverview = async () => {
    try {
      const { data } = await api.get(`/pipeline/overview`);
      setRows(data?.spaces || []);
    } catch (e: any) {
      setErr(e?.response?.data?.message || String(e));
    }
  };

  // seed=true only on explicit space selection / save — NEVER on the
  // 5s poll or slug onBlur, otherwise it wipes what the user is typing
  // into the label/aliases inputs.
  const loadStatus = async (s: string, seed = false) => {
    if (!s) return;
    try {
      const { data } = await api.get(`/pipeline/${s}/status`);
      setSt(data);
      if (seed) {
        // Seed the repo list from the server's source of truth on
        // explicit selection / save (not on the 5s poll, otherwise we
        // overwrite what the user is typing). Prefer the new `repos`
        // JSONB array; fall back to the legacy single `repo`/`branch`.
        const fromArr: RepoEntry[] | undefined = (data?.repos || [])
          .map((r: any) => ({
            repo: (r?.repo || "").trim(),
            branch: (r?.branch || "main").trim() || "main",
          }))
          .filter((r: RepoEntry) => r.repo);
        if (fromArr && fromArr.length) {
          setRepos(fromArr);
        } else if (data?.repo) {
          setRepos([
            { repo: data.repo, branch: data.branch || "main" },
          ]);
        } else {
          setRepos([{ repo: "", branch: "main" }]);
        }
        setLabel(data?.label || "");
        setAliases((data?.aliases || []).join(", "));
      }
    } catch (e: any) {
      setErr(e?.response?.data?.message || String(e));
    }
  };

  // On mount (incl. after refresh / re-login): restore the last viewed
  // space and the cross-space overview. Progress itself is server-side
  // (DB-derived) so it is never lost by a reload.
  useEffect(() => {
    loadOverview();
    if (slug) loadStatus(slug, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a run is active so the progress bar advances live.
  // A fresh interval is created each time this effect runs and always
  // cleared on cleanup — no shared ref to go stale (a previous
  // ref-guard left the interval cleared-but-not-null after the first
  // dependency change, which silently killed live updates until a
  // manual page refresh).
  useEffect(() => {
    const running =
      st?.status === "running" || st?.progress?.run_status === "running";
    if (!running) return;
    const id = setInterval(() => {
      loadStatus(slug);
      loadOverview();
    }, 5000);
    return () => clearInterval(id);
  }, [st?.status, st?.progress?.run_status, slug]);

  const pick = (s: string) => {
    setSlug(s);
    localStorage.setItem(LS_SLUG, s);
    setRepos([{ repo: "", branch: "main" }]);
    loadStatus(s, true);
  };

  const configure = async () => {
    setErr("");
    setBusy(true);
    try {
      const clean = repos
        .map((r) => ({
          repo: (r.repo || "").trim(),
          branch: (r.branch || "main").trim() || "main",
        }))
        .filter((r) => r.repo);
      await api.post(`/pipeline/${slug}/configure`, { repos: clean });
      localStorage.setItem(LS_SLUG, slug);
      await loadStatus(slug);
      await loadOverview();
    } catch (e: any) {
      setErr(e?.response?.data?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDisplay = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.post(`/pipeline/${slug}/display`, {
        label,
        aliases: aliases
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
      });
      await loadStatus(slug, true);
      await loadOverview();
    } catch (e: any) {
      setErr(e?.response?.data?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const run = async (phase: string) => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/pipeline/${slug}/run`, { phase });
      if (data?.error) setErr(data.error);
      await loadStatus(slug);
      await loadOverview();
    } catch (e: any) {
      setErr(e?.response?.data?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const pg = st?.progress;
  const pct =
    pg && pg.total
      ? Math.min(100, Math.round(((pg.done || 0) / pg.total) * 100))
      : 0;
  const stepIdx = STEPS.findIndex((s) => s.key === st?.current_phase);
  const step = stepIdx >= 0 ? STEPS[stepIdx] : null;
  const running = st?.status === "running";

  return (
    <>
      <Helmet>
        <title>Docs Pipeline</title>
      </Helmet>
      <SettingsTitle title="Docs Pipeline" />
      <Stack maw={860}>
        <Text size="sm" c="dimmed">
          Конфиг хранится per-space (ключ — slug пространства) и не
          перезатирается другими space. Ниже — все пространства с
          пайплайном; выберите строку, чтобы управлять конкретным.
        </Text>

        <Divider label="Активные пространства" />
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Space</Table.Th>
              <Table.Th>Репозиторий</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Материалов</Table.Th>
              <Table.Th>+ за неделю</Table.Th>
              <Table.Th>Последнее обновление</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text size="sm" c="dimmed">
                    Нет настроенных пространств.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((r) => (
              <Table.Tr key={r.space_slug}>
                <Table.Td>
                  <Anchor
                    component="button"
                    type="button"
                    onClick={() => pick(r.space_slug!)}
                  >
                    {r.space_slug}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {/* multi-repo: show all entries; legacy single-repo
                       rows still render via the fallback */}
                    {((r.repos || []).filter((x) => x?.repo)
                      .map((x) => x.repo).join(", "))
                      || r.repo || "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={color(r.status)} size="sm">
                    {r.status || "idle"}
                  </Badge>
                </Table.Td>
                <Table.Td>{r.pages ?? 0}</Table.Td>
                <Table.Td>
                  {r.pages_week ? `+${r.pages_week}` : "0"}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {fmt(r.last_page_at || r.last_run_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Divider label="Управление пространством" />
        <TextInput
          label="Space slug"
          placeholder="proxysellerold"
          value={slug}
          onChange={(e) => {
            const v = e.currentTarget.value.trim();
            setSlug(v);
            localStorage.setItem(LS_SLUG, v);
          }}
          onBlur={() => loadStatus(slug)}
        />
        <Text size="sm" c="dimmed">
          Репозитории проекта. Можно добавить несколько — например
          backend и frontend; webhook любого из них роутится в этот
          space, а инвентаризация строит единый граф связей по всем.
        </Text>
        <Stack gap="xs">
          {repos.map((r, i) => (
            <Group key={i} align="flex-end" gap="xs" wrap="nowrap">
              <TextInput
                label={i === 0 ? "Репозиторий (org/repo)" : undefined}
                placeholder="forproxyband/proxy-seller-old"
                value={r.repo}
                onChange={(e) => {
                  const v = e.currentTarget.value.trim();
                  setRepos(repos.map((x, j) =>
                    j === i ? { ...x, repo: v } : x));
                }}
                style={{ flex: 1 }}
              />
              <TextInput
                label={i === 0 ? "Ветка" : undefined}
                value={r.branch || "main"}
                onChange={(e) => {
                  const v = e.currentTarget.value.trim();
                  setRepos(repos.map((x, j) =>
                    j === i ? { ...x, branch: v } : x));
                }}
                style={{ width: 120 }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={repos.length === 1 && !r.repo}
                onClick={() =>
                  repoListChanged(repos.filter((_, j) => j !== i))
                }
                aria-label="Удалить репозиторий"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            leftSection={<IconPlus size={14} />}
            variant="subtle"
            size="xs"
            onClick={() =>
              setRepos([...repos, { repo: "", branch: "main" }])
            }
            style={{ alignSelf: "flex-start" }}
          >
            Добавить репозиторий
          </Button>
        </Stack>
        <Group>
          <Button
            onClick={configure}
            disabled={
              !slug || busy ||
              !repos.some((r) => (r.repo || "").trim())
            }
            variant="default"
          >
            Сохранить конфиг
          </Button>
          <Button
            onClick={() => run("full")}
            disabled={!slug || busy || st?.status === "running"}
            color="blue"
          >
            Запустить инвентаризацию (full)
          </Button>
          {(st?.status === "failed" || st?.status === "done") && (
            <Button
              onClick={() => run("full")}
              disabled={!slug || busy}
              variant="outline"
              color={st?.status === "failed" ? "red" : "gray"}
            >
              Перезапустить
            </Button>
          )}
        </Group>

        <Divider label="Slack-бот: имя проекта" />
        <Text size="sm" c="dimmed">
          Как разработчики называют проект в Slack-боте. Кнопки выбора и
          `/docs set-space` показывают это имя; алиасы (через запятую)
          тоже принимаются. Применяется сразу, без редеплоя.
        </Text>
        <Group grow>
          <TextInput
            label="Отображаемое имя"
            placeholder="IPv4 Proxy"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
          <TextInput
            label="Алиасы (через запятую)"
            placeholder="ipv4, proxy-ipv4"
            value={aliases}
            onChange={(e) => setAliases(e.currentTarget.value)}
          />
        </Group>
        <Group>
          <Button
            onClick={saveDisplay}
            disabled={!slug || busy}
            variant="default"
          >
            Сохранить имя для бота
          </Button>
        </Group>

        <Divider label="Отдельные фазы" />
        <Group gap="xs">
          {PHASES.map((p) => (
            <Button
              key={p}
              size="xs"
              variant="light"
              disabled={!slug || busy || st?.status === "running"}
              onClick={() => run(p)}
            >
              {p}
            </Button>
          ))}
        </Group>

        <Divider label="Статус" />
        {err && (
          <Text size="sm" c="red">
            {err}
          </Text>
        )}
        {st ? (
          <Stack gap={6}>
            <Group gap="xs">
              <Text size="sm">Статус:</Text>
              <Badge color={color(st.status)}>
                {st.status || "unconfigured"}
              </Badge>
              {st.current_phase && (
                <Text size="sm" c="dimmed">
                  фаза: {st.current_phase}
                </Text>
              )}
            </Group>
            {running && pg && pg.total && (
              st?.current_phase === "generate" ||
              st?.current_phase === "verify" ||
              st?.current_phase === "route-drafts"
            ) ? (
              <Stack gap={2}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Прогресс генерации
                    {pg.phase ? ` (${pg.phase})` : ""}
                  </Text>
                  <Text size="sm" fw={600}>
                    {pg.done ?? 0}/{pg.total}
                  </Text>
                </Group>
                <Progress value={pct} animated />
              </Stack>
            ) : running ? (
              <Stack gap={2}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {step
                      ? `Шаг ${stepIdx + 1}/${STEPS.length}: ${step.label}`
                      : `Выполняется: ${st.current_phase || "…"}`}
                  </Text>
                  {pg && pg.files_scanned ? (
                    <Text size="sm" fw={600}>
                      файлов: {pg.files_after_filter ?? 0}/
                      {pg.files_scanned}
                    </Text>
                  ) : null}
                </Group>
                <Progress
                  value={
                    step ? ((stepIdx + 1) / STEPS.length) * 100 : 100
                  }
                  striped
                  animated
                />
                <Text size="xs" c="dimmed">
                  Идёт инвентаризация. Полоса «готово/всего» появится на
                  шаге генерации; ранние фазы (фильтрация/классификация/
                  группировка/планирование) длительные — это нормально.
                </Text>
              </Stack>
            ) : pg && pg.total ? (
              <Text size="sm" c="dimmed">
                Инвентаризация завершена: {pg.done ?? 0}/{pg.total}.
                Ожидаются обновления из репозитория.
              </Text>
            ) : null}
            {(st.repos?.length || st.repo) && (
              <Text size="sm" c="dimmed">
                {st.repos && st.repos.length
                  ? "repos: " +
                    st.repos
                      .filter((r) => r?.repo)
                      .map((r) => `${r.repo} @ ${r.branch || "main"}`)
                      .join(", ")
                  : `repo: ${st.repo} @ ${st.branch}`}
              </Text>
            )}
            <Text size="sm" c="dimmed">
              материалов: {st.pages ?? 0} (за неделю +{st.pages_week ?? 0})
            </Text>
            {st.last_run_at && (
              <Text size="sm" c="dimmed">
                last run: {fmt(st.last_run_at)}
              </Text>
            )}
            {st.message && (
              <Code block style={{ whiteSpace: "pre-wrap" }}>
                {st.message}
              </Code>
            )}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            Выберите пространство в таблице или введите slug.
          </Text>
        )}
      </Stack>
    </>
  );
}
